"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/lib/cart-context";
import { resolvePostAuthRedirect } from "@/lib/post-auth-redirect";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

type View = "login" | "register";

export default function ConnexionPage() {
  const router = useRouter();
  const { cartCount } = useCart();
  const [view, setView] = useState<View>("login");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [cguAccepted, setCguAccepted] = useState(false);
  const [newsletter, setNewsletter] = useState(false);

  const getPostAuthRedirect = () =>
    resolvePostAuthRedirect({
      cartCount,
      search: window.location.search,
      storedCart: localStorage.getItem("toolouer_cart"),
    });

  useEffect(() => {
    const googleError = new URLSearchParams(window.location.search).get(
      "google_error"
    );
    if (!googleError) return;

    const messages: Record<string, string> = {
      cancelled: "Connexion Google annulée.",
      configuration: "La connexion Google n’est pas encore configurée.",
      invalid: "La demande de connexion Google a expiré. Veuillez réessayer.",
      failed: "La connexion Google a échoué. Veuillez réessayer.",
    };
    const timer = window.setTimeout(() => {
      setError(messages[googleError] || messages.failed);
    }, 0);
    window.history.replaceState({}, "", window.location.pathname);

    return () => window.clearTimeout(timer);
  }, []);

  const handleGoogleLogin = () => {
    setError(null);
    setGoogleLoading(true);
    const returnTo = getPostAuthRedirect();
    window.location.assign(
      `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  const googleAuthBlock = (
    <>
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading || googleLoading}
        className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
          <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.91h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.4Z" />
          <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.42l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
          <path fill="#FBBC05" d="M6.39 13.87A6.02 6.02 0 0 1 6.08 12c0-.65.11-1.28.31-1.87V7.51H3.04A10 10 0 0 0 2 12c0 1.61.38 3.14 1.04 4.49l3.35-2.62Z" />
          <path fill="#EA4335" d="M12 6c1.47 0 2.79.5 3.82 1.49l2.87-2.87A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.96 5.51l3.35 2.62C7.18 7.76 9.39 6 12 6Z" />
        </svg>
        {googleLoading ? "Redirection vers Google…" : "Continuer avec Google"}
      </button>
      <p className="mt-3 text-center text-xs leading-5 text-gray-500">
        En continuant, vous acceptez nos{" "}
        <a href="/docs/cgu.pdf" target="_blank" rel="noopener noreferrer" className="underline hover:text-secondary">
          CGU
        </a>{" "}
        et notre{" "}
        <a href="/docs/mentions-legales.pdf" target="_blank" rel="noopener noreferrer" className="underline hover:text-secondary">
          politique de confidentialité
        </a>.
      </p>
      <div className="my-5 flex items-center gap-3" aria-hidden="true">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs uppercase tracking-wide text-gray-400">ou</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
    </>
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Erreur de connexion");
        return;
      }

      router.push(getPostAuthRedirect());
    } catch {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!cguAccepted) {
      setError("Vous devez accepter les CGU");
      return;
    }

    if (registerPassword.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: registerEmail,
          password: registerPassword,
          first_name: firstName,
          last_name: lastName,
          phone,
          newsletter,
          cgu_accepted: cguAccepted,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Erreur lors de l'inscription");
        return;
      }

      if (data.requiresVerification) {
        const redirect = getPostAuthRedirect();
        router.push(`/verification?redirect=${encodeURIComponent(redirect)}`);
      } else {
        router.push(getPostAuthRedirect());
      }
    } catch {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link href="/">
            <Image
              src="/logo_green_font.png"
              alt="Toolouer"
              width={180}
              height={50}
              className="h-12 w-auto"
            />
          </Link>
        </div>

        {view === "login" ? (
          /* ---- VUE CONNEXION ---- */
          <div className="bg-white rounded-xl shadow-sm border p-8">
            <h1 className="text-2xl font-bold text-center mb-6">Se connecter</h1>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {googleAuthBlock}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail *
                </label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="h-11"
                />
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Votre mot de passe"
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-secondary text-white hover:bg-secondary/90 font-semibold"
              >
                {loading ? "Connexion..." : "Me connecter"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Pas encore de compte ?{" "}
                <button
                  onClick={() => {
                    setView("register");
                    setError(null);
                  }}
                  className="text-secondary font-semibold hover:underline"
                >
                  Créer un compte
                </button>
              </p>
            </div>
          </div>
        ) : (
          /* ---- VUE INSCRIPTION ---- */
          <div className="bg-white rounded-xl shadow-sm border p-8">
            <button
              onClick={() => {
                setView("login");
                setError(null);
              }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour
            </button>

            <h1 className="text-2xl font-bold text-center mb-6">Créer un compte</h1>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {googleAuthBlock}

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom (facultatif)
                  </label>
                  <Input
                    id="first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Prénom"
                    className="h-11"
                  />
                </div>
                <div>
                  <label htmlFor="last-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom (facultatif)
                  </label>
                  <Input
                    id="last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Nom"
                    className="h-11"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail *
                </label>
                <Input
                  id="register-email"
                  type="email"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="h-11"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone (facultatif)
                </label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78"
                  className="h-11"
                />
              </div>

              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <div className="relative">
                  <Input
                    id="register-password"
                    type={showPassword ? "text" : "password"}
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                    minLength={6}
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cguAccepted}
                    onChange={(e) => setCguAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary"
                  />
                  <span className="text-sm text-gray-600">
                    J&apos;accepte la{" "}
                    <a href="/docs/mentions-legales.pdf" target="_blank" rel="noopener noreferrer" className="text-secondary underline">
                      politique de confidentialité
                    </a>{" "}
                    et les{" "}
                    <a href="/docs/cgu.pdf" target="_blank" rel="noopener noreferrer" className="text-secondary underline">
                      CGU
                    </a>{" "}
                    *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletter}
                    onChange={(e) => setNewsletter(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary"
                  />
                  <span className="text-sm text-gray-600">
                    M&apos;inscrire à la newsletter Toolouer
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-secondary text-white hover:bg-secondary/90 font-semibold mt-2"
              >
                {loading ? "Inscription..." : "Me connecter"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Déjà un compte ?{" "}
                <button
                  onClick={() => {
                    setView("login");
                    setError(null);
                  }}
                  className="text-secondary font-semibold hover:underline"
                >
                  Se connecter
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
